/**
 * Scroll Fade-In Animation
 * Adds fade-in effect to sections as they come into view while scrolling
 */

class ScrollFadeIn {
  constructor() {
    this.observer = null;
    this.init();
  }

  init() {
    // Only run on home page
    if (!this.isHomePage()) {
      return;
    }

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupObserver());
    } else {
      this.setupObserver();
    }
  }

  isHomePage() {
    // Check if we're on the home page
    return document.body.classList.contains('template-index') || 
           window.location.pathname === '/' || 
           window.location.pathname === '/index' ||
           document.body.classList.contains('template-index') ||
           (window.location.pathname === '' && window.location.search === '');
  }

  setupObserver() {
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      // Fallback for older browsers - just show all sections
      this.showAllSections();
      return;
    }

    // Track scroll speed for adaptive behavior
    let lastScrollTime = Date.now();
    let scrollSpeed = 0;

    // Monitor scroll speed
    window.addEventListener('scroll', () => {
      const now = Date.now();
      const timeDiff = now - lastScrollTime;
      scrollSpeed = timeDiff < 30 ? 'fast' : 'normal'; // Less than 30ms between scrolls = fast
      lastScrollTime = now;
    });

    // Create intersection observer
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add fade-in-visible class when section comes into view
            entry.target.classList.add('fade-in-visible');
            
            // If user is scrolling fast, make animation even faster
            if (scrollSpeed === 'fast') {
              entry.target.style.transitionDuration = '0.2s';
            }
            
            // Stop observing this element once it's visible
            this.observer.unobserve(entry.target);
          }
        });
      },
      {
        // Trigger animation when element is 15% visible (balanced trigger)
        threshold: 0,
        // Start animation 80px before element comes into view (balanced trigger)
        rootMargin: '0px 0px 0px 0px'
      }
    );

    // Add scroll-fade-in class to all sections and observe them
    this.addFadeInToSections();
    
    // Also specifically target Instagram sections
    this.addFadeInToInstagramSections();
  }

  addFadeInToInstagramSections() {
    // Specifically target Instagram sections
    const instagramSections = document.querySelectorAll('.sec__instagram, [class*="instagram"]');
    
    instagramSections.forEach((section, index) => {
      // Skip if already has fade-in class
      if (section.classList.contains('scroll-fade-in')) {
        return;
      }

      // Skip if it's a footer section
      if (this.isFooterSection(section)) {
        return;
      }

      // Add scroll-fade-in class
      section.classList.add('scroll-fade-in');
      
      // Add delay for staggered effect
      section.style.transitionDelay = `${index * 0.02}s`;
      
      // Start observing this section
      this.observer.observe(section);
    });
  }

  addFadeInToSections() {
    // Target main sections but exclude header, hero, and footer
    const sections = document.querySelectorAll('main section, .page-width > section, [class*="section-"]');
    
    sections.forEach((section, index) => {
      // Skip if section already has fade-in class
      if (section.classList.contains('scroll-fade-in')) {
        return;
      }

      // Skip header sections
      if (this.isHeaderSection(section)) {
        return;
      }

      // Skip hero sections
      if (this.isHeroSection(section)) {
        return;
      }

      // Skip footer sections
      if (this.isFooterSection(section)) {
        return;
      }

      // Add scroll-fade-in class
      section.classList.add('scroll-fade-in');
      
      // Add smooth delay for staggered effect
      section.style.transitionDelay = `${index * 0.02}s`;
      
      // Start observing this section
      this.observer.observe(section);
    });
  }

  isHeaderSection(section) {
    // Check for header-related classes and IDs
    const headerSelectors = [
      'header',
      '[class*="header"]',
      '[id*="header"]',
      '.header-group',
      '.announcement-bar'
    ];
    
    return headerSelectors.some(selector => 
      section.matches(selector) || 
      section.querySelector(selector)
    );
  }

  isHeroSection(section) {
    // Check for specific hero section only
    const heroSelectors = [
      '#hero-section-1',
      '.hero-section-1',
      '[id="hero-section-1"]'
    ];
    
    // Also check if it's the first section (likely hero)
    const isFirstSection = section === document.querySelector('main section:first-child');
    
    return heroSelectors.some(selector => 
      section.matches(selector) || 
      section.querySelector(selector)
    ) || isFirstSection;
  }

  isFooterSection(section) {
    // Check for footer-related classes and IDs
    const footerSelectors = [
      'footer',
      '[class*="footer"]',
      '[id*="footer"]',
      '.footer-group'
    ];
    
    return footerSelectors.some(selector => 
      section.matches(selector) || 
      section.querySelector(selector)
    );
  }

  showAllSections() {
    // Fallback for browsers without IntersectionObserver support
    const sections = document.querySelectorAll('main section, .page-width > section, [class*="section-"]');
    sections.forEach((section) => {
      // Skip header, hero, and footer sections
      if (!this.isHeaderSection(section) && 
          !this.isHeroSection(section) && 
          !this.isFooterSection(section)) {
        section.classList.add('scroll-fade-in', 'fade-in-visible');
      }
    });
  }

  // Method to refresh observer (useful for dynamic content)
  refresh() {
    if (this.observer) {
      this.observer.disconnect();
      this.addFadeInToSections();
    }
  }

  // Method to destroy observer
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

// Initialize scroll fade-in when DOM is ready
const scrollFadeIn = new ScrollFadeIn();

// Export for potential use in other scripts
window.scrollFadeIn = scrollFadeIn;
